/**
 * usePresenceStream — SSE hook for the admin_monitoring presence dashboard.
 *
 * Opens an EventSource to /api/admin-monitoring/presence/stream/ and dispatches
 * presence_update + force_logout events to callbacks.
 *
 * Auth:
 *   - JWT lives in an HttpOnly cookie, so the stream is opened with
 *     `withCredentials: true` and the cookie authenticates it — no token is
 *     ever placed in the URL (matches useHuddleStream). Token rotation is
 *     handled transparently by the cookie + the refresh interceptor.
 *
 * Reliability:
 *   - On a permanent close (readyState === 2, i.e. server rejected) it
 *     schedules a single 5s reconnect rather than leaving the dashboard
 *     stuck on a stale snapshot.
 */
import { useEffect, useRef } from 'react';
import { silentRefresh } from '../api/api';

// Resolve the API base the SAME way the axios client does (src/api/api.js):
// REACT_APP_API_URL when set, else a same-origin relative "/api". Never a
// hardcoded localhost — in a prod build that would point EventSource at the
// viewer's own machine. (REACT_APP_API_BASE_URL kept first for back-compat.)
const BASE_URL =
  process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '/api';

const readCompanyId = () => {
  try {
    const raw = localStorage.getItem('active_company');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return parsed?.id || parsed?.company_id || '';
  } catch {
    return '';
  }
};

export const usePresenceStream = (enabled, callbacks = {}) => {
  const esRef = useRef(null);
  const callbacksRef = useRef(callbacks);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    callbacksRef.current = callbacks;
  });

  useEffect(() => {
    if (!enabled) return undefined;

    const companyId = readCompanyId();
    let cancelled = false;

    const open = () => {
      if (cancelled) return;
      const companyParam = companyId
        ? `?company_id=${encodeURIComponent(companyId)}`
        : '';
      const url = `${BASE_URL}/admin-monitoring/presence/stream/${companyParam}`;
      // withCredentials sends the HttpOnly JWT cookie so no token in the URL.
      const es = new EventSource(url, { withCredentials: true });
      esRef.current = es;

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const cb = callbacksRef.current;
          switch (data.type) {
            case 'connected':
              cb.onConnected?.(data);
              break;
            case 'presence_update':
              cb.onPresenceUpdate?.(data);
              break;
            case 'force_logout':
              cb.onForceLogout?.(data);
              break;
            default:
              break;
          }
        } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('[Presence SSE] parse error:', e);
          }
        }
      };

      es.onerror = () => {
        // EventSource auto-reconnects on transient transport errors. When the
        // server rejected the connection outright readyState becomes CLOSED;
        // schedule a single retry so an expired or rotated cookie doesn't
        // leave us silently disconnected.
        if (es.readyState === 2 && !cancelled) {
          try { es.close(); } catch { /* noop */ }
          esRef.current = null;
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = setTimeout(() => {
            reconnectTimerRef.current = null;
            // Refresh BEFORE reopening. The usual cause of an outright
            // rejection here is a 401: the access cookie is deleted after 15
            // minutes, and EventSource never reaches the axios interceptor that
            // would renew it, so retrying alone reconnects with no credentials
            // and 401s again every 5s forever. A failed refresh (genuinely
            // logged out) still falls through to open(), which fails the same
            // way and re-arms this timer — no new failure mode.
            silentRefresh()
              .catch(() => { /* not signed in: let open() fail and retry */ })
              .then(() => { if (!cancelled) open(); });
          }, 5000);
        }
      };
    };

    open();

    return () => {
      cancelled = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        try { esRef.current.close(); } catch { /* noop */ }
        esRef.current = null;
      }
    };
  }, [enabled]);
};

export default usePresenceStream;
